/*!
 * Mobile Breakpoint Validation Script
 * Tests and validates responsive behavior across all Stickerine sections
 * Version: 1.0.0
 */

window.StickerineMobileValidator = {
  
  // Standard breakpoints used throughout the theme
  breakpoints: {
    mobile: 320,
    mobileLarge: 480,
    tablet: 768,
    laptop: 1024,
    laptopLarge: 1366,
    desktop: 1440,
    desktopLarge: 1920
  },

  // Sections to validate
  sectionsToTest: [
    'stickerine-hero',
    'stickerine-category-icons', 
    'stickerine-deals',
    'stickerine-process',
    'stickerine-testimonials-with-icons',
    'stickerine-shop-collection-carousel',
    'stickerine-two-column-cards',
    'stickerine-announcement-bar'
  ],

  // Test results
  results: {
    passed: 0,
    failed: 0,
    warnings: 0,
    details: []
  },

  /**
   * Run comprehensive mobile validation
   */
  async runValidation() {
    console.log('🔍 Starting mobile breakpoint validation...');
    this.clearResults();
    
    for (const sectionName of this.sectionsToTest) {
      const section = document.querySelector(`[data-section-id*="${sectionName}"], .${sectionName}`);
      
      if (section) {
        await this.validateSection(section, sectionName);
      } else {
        this.addResult('warning', sectionName, 'Section not found on current page');
      }
    }
    
    this.displayResults();
    return this.results;
  },

  /**
   * Validate individual section at different breakpoints
   */
  async validateSection(section, sectionName) {
    console.log(`📱 Testing ${sectionName}...`);
    
    for (const [breakpointName, width] of Object.entries(this.breakpoints)) {
      // Simulate viewport width
      await this.simulateViewport(width);
      
      // Test section at this breakpoint
      const testResult = this.testSectionAtBreakpoint(section, sectionName, breakpointName, width);
      
      if (testResult.issues.length > 0) {
        testResult.issues.forEach(issue => {
          this.addResult('failed', sectionName, `${breakpointName} (${width}px): ${issue}`);
        });
      } else {
        this.addResult('passed', sectionName, `${breakpointName} (${width}px): ✓ All tests passed`);
      }
      
      // Small delay for DOM updates
      await this.delay(100);
    }
  },

  /**
   * Test section behavior at specific breakpoint
   */
  testSectionAtBreakpoint(section, sectionName, breakpointName, width) {
    const issues = [];
    
    // Test 1: Check for horizontal overflow
    if (this.hasHorizontalOverflow(section)) {
      issues.push('Horizontal overflow detected');
    }
    
    // Test 2: Check minimum heights
    if (this.isTooShort(section, width)) {
      issues.push('Section height too short for content');
    }
    
    // Test 3: Check text readability
    if (this.hasReadabilityIssues(section)) {
      issues.push('Text readability issues (too small or overlapping)');
    }
    
    // Test 4: Check interactive elements
    if (this.hasTouchTargetIssues(section)) {
      issues.push('Touch targets too small (< 44px)');
    }
    
    // Test 5: Check carousel behavior (if applicable)
    if (section.querySelector('.swiper')) {
      const carouselIssues = this.validateCarousel(section, width);
      issues.push(...carouselIssues);
    }
    
    // Test 6: Section-specific tests
    const specificIssues = this.runSectionSpecificTests(section, sectionName, breakpointName, width);
    issues.push(...specificIssues);
    
    return { issues };
  },

  /**
   * Section-specific validation rules
   */
  runSectionSpecificTests(section, sectionName, breakpointName, width) {
    const issues = [];
    
    switch (sectionName) {
      case 'stickerine-hero':
        // Hero should stack on mobile
        if (width < this.breakpoints.tablet) {
          const container = section.querySelector('.stickerine-hero__container');
          if (container && window.getComputedStyle(container).gridTemplateColumns !== '1fr') {
            issues.push('Hero not stacking properly on mobile');
          }
        }
        break;
        
      case 'stickerine-deals':
        // Deal cards should use fixed width on mobile
        if (width < 750) {
          const slides = section.querySelectorAll('.swiper-slide');
          slides.forEach((slide, index) => {
            const computedWidth = window.getComputedStyle(slide).width;
            if (!computedWidth.includes('306px')) { // 85% of 360px
              issues.push(`Deal card ${index + 1} not using mobile fixed width`);
            }
          });
        }
        break;
        
      case 'stickerine-category-icons':
        // Category icons should have proper spacing
        const grid = section.querySelector('.category-grid');
        if (grid) {
          const gap = window.getComputedStyle(grid).gap;
          if (width < this.breakpoints.tablet && parseFloat(gap) > 24) {
            issues.push('Category grid gap too large for mobile');
          }
        }
        break;
    }
    
    return issues;
  },

  /**
   * Check for horizontal overflow
   */
  hasHorizontalOverflow(element) {
    return element.scrollWidth > element.clientWidth;
  },

  /**
   * Check if section is too short for its content
   */
  isTooShort(section, width) {
    const minHeights = {
      320: 300,   // Mobile minimum
      768: 400,   // Tablet minimum
      1024: 500   // Desktop minimum
    };
    
    const minHeight = width < 768 ? minHeights[320] : 
                     width < 1024 ? minHeights[768] : 
                     minHeights[1024];
    
    return section.offsetHeight < minHeight;
  },

  /**
   * Check text readability
   */
  hasReadabilityIssues(section) {
    const textElements = section.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, a');
    
    for (const element of textElements) {
      const styles = window.getComputedStyle(element);
      const fontSize = parseFloat(styles.fontSize);
      
      // Check minimum font sizes
      if (fontSize < 14) {
        return true;
      }
      
      // Check for overlapping elements
      if (this.isOverlapping(element)) {
        return true;
      }
    }
    
    return false;
  },

  /**
   * Check touch target sizes
   */
  hasTouchTargetIssues(section) {
    const interactiveElements = section.querySelectorAll('button, a, input, .swiper-button-next, .swiper-button-prev');
    
    for (const element of interactiveElements) {
      const rect = element.getBoundingClientRect();
      if (rect.width < 44 || rect.height < 44) {
        return true;
      }
    }
    
    return false;
  },

  /**
   * Validate carousel behavior
   */
  validateCarousel(section, width) {
    const issues = [];
    const swiper = section.querySelector('.swiper');
    
    if (!swiper) return issues;
    
    // Check if carousel is initialized
    if (!swiper.swiper) {
      issues.push('Carousel not initialized');
      return issues;
    }
    
    // Check slide widths
    const slides = swiper.querySelectorAll('.swiper-slide');
    if (slides.length > 0) {
      const firstSlide = slides[0];
      const slideWidth = firstSlide.getBoundingClientRect().width;
      
      // Mobile carousel slides shouldn't be too wide
      if (width < 750 && slideWidth > width * 0.9) {
        issues.push('Carousel slides too wide for mobile viewport');
      }
    }
    
    return issues;
  },

  /**
   * Check if element is overlapping with others
   */
  isOverlapping(element) {
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const elementBelow = document.elementFromPoint(centerX, centerY);
    
    return elementBelow && elementBelow !== element && !element.contains(elementBelow);
  },

  /**
   * Simulate different viewport widths
   */
  async simulateViewport(width) {
    // This is a simulation - in real testing you'd use browser dev tools
    // For now, we'll trigger resize events to test responsive behavior
    
    // Set viewport meta temporarily
    let viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport ? viewport.content : '';
    
    if (viewport) {
      viewport.content = `width=${width}, initial-scale=1`;
    }
    
    // Trigger resize event
    window.dispatchEvent(new Event('resize'));
    
    // Reset viewport
    if (viewport && originalContent) {
      viewport.content = originalContent;
    }
  },

  /**
   * Utility functions
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  clearResults() {
    this.results = { passed: 0, failed: 0, warnings: 0, details: [] };
  },

  addResult(type, section, message) {
    this.results[type]++;
    this.results.details.push({
      type,
      section,
      message,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Display validation results
   */
  displayResults() {
    console.log('\n📊 Mobile Breakpoint Validation Results:');
    console.log(`✅ Passed: ${this.results.passed}`);
    console.log(`❌ Failed: ${this.results.failed}`);
    console.log(`⚠️  Warnings: ${this.results.warnings}`);
    
    if (this.results.details.length > 0) {
      console.log('\nDetailed Results:');
      this.results.details.forEach(result => {
        const icon = result.type === 'passed' ? '✅' : 
                    result.type === 'failed' ? '❌' : '⚠️';
        console.log(`${icon} [${result.section}] ${result.message}`);
      });
    }
    
    // Calculate score
    const total = this.results.passed + this.results.failed;
    const score = total > 0 ? (this.results.passed / total * 100).toFixed(1) : 0;
    
    console.log(`\n🎯 Overall Score: ${score}%`);
    
    if (score >= 90) {
      console.log('🏆 Excellent mobile optimization!');
    } else if (score >= 75) {
      console.log('👍 Good mobile optimization with room for improvement');
    } else {
      console.log('⚡ Mobile optimization needs attention');
    }
    
    return this.results;
  },

  /**
   * Quick validation for specific section
   */
  validateSingleSection(sectionName) {
    const section = document.querySelector(`[data-section-id*="${sectionName}"], .${sectionName}`);
    
    if (!section) {
      console.log(`❌ Section "${sectionName}" not found`);
      return false;
    }
    
    console.log(`🔍 Quick validation for ${sectionName}...`);
    return this.validateSection(section, sectionName);
  }
};

// Make available globally for console testing
window.validateMobile = () => window.StickerineMobileValidator.runValidation();
window.validateSection = (name) => window.StickerineMobileValidator.validateSingleSection(name);